import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { fetchCmsCollection, findCmsPageBySlug } from '../lib/cms.js'

const SiteDataContext = createContext(null)

const storyOrder = [
  'lars-damgaard',
  'leif-nyhus-christensen',
  'charlotte-terkelsen',
  'dyveke-brandenburg',
  'michael-nielsen',
  'zivile-abraitiene',
  'doris-mygind',
  'line-damgaard-andersen',
  'martin-joergensen',
  'joergen-jensen',
  'gunnar-hansen',
  'karen-bagge',
  'astrid-pollmann',
  'jesper-joergensen',
  'soeren-soerensen',
  'steen-johansen',
  'allan-christensen',
  'svend-aage-egelund',
  'birthe-jensen-kjeldsen',
  'stine-juul',
  'caroline-zederkof',
  'heidi-jensen',
  'dorte-ahlgren',
  'bettina-therkildsen',
  'john-jensen',
  'brian-terp-bank',
  'pernille-moegelvang-dohn',
  'jan-hansen',
  'lennart-kjaer',
  'aase-brandt-nielsen',
  'grethe',
  'kirsten-vogel',
  'heidi-koust',
  'bodil-melin',
  'zunja-zuleima-anuuk-bakmoesdaal',
  'tanja-lykke-soerensen',
  'anders-trasborg-thomsen',
  'jens-peter-albrechtsen',
  'iben-k-olesen',
  'erik-w-pedersen',
  'pernille-dohn',
  'tanja-juul-soerensen',
  'thomas-vahle',
  'patrick-power',
  'joan-borroe-henningsen'
]

async function loadCollection(collectionName) {
  let allItems = []
  let offset = 0
  const limit = 25
  let hasMore = true

  while (hasMore) {
    const response = await fetchCmsCollection(collectionName, {
      limit,
      offset,
      sort: 'updatedAt',
      order: 'desc',
    })

    const items = response?.items ?? []
    allItems = [...allItems, ...items]

    const total = response?.total ?? 0
    offset += limit

    if (items.length === 0 || allItems.length >= total || offset >= total) {
      hasMore = false
    }
  }

  return allItems
}

export function SiteDataProvider({ children }) {
  const [pages, setPages] = useState([])
  const [patienthistorier, setPatienthistorier] = useState([])
  const [galleries, setGalleries] = useState([])
  const [behandlingsmetoder, setBehandlingsmetoder] = useState([])
  const [sygdommeLidelser, setSygdommeLidelser] = useState([])
  const [accordions, setAccordions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true

    async function run() {
      try {
        setLoading(true)
        setError('')

        const [pagesData, storiesData, galleriesData, treatmentsData, illnessesData, accordionsData] = await Promise.all([
          loadCollection('pages'),
          loadCollection('patienthistorier'),
          loadCollection('galleri'),
          loadCollection('behandlingsmetoder'),
          loadCollection('sygdomme_lidelser'),
          loadCollection('accordions'),
        ])

        if (!alive) return

        // Sort treatments by order
        const sortedTreatments = [...treatmentsData].sort((a, b) => {
          const orderA = typeof a?.order === 'number' ? a.order : 999
          const orderB = typeof b?.order === 'number' ? b.order : 999
          return orderA - orderB
        })

        // Sort illnesses / symptoms by order
        const sortedIllnesses = [...illnessesData].sort((a, b) => {
          const orderA = typeof a?.order === 'number' ? a.order : 999
          const orderB = typeof b?.order === 'number' ? b.order : 999
          return orderA - orderB
        })

        // Sort patient stories according to the predefined order
        const sortedStories = [...storiesData].sort((a, b) => {
          const slugA = a?.slug?.da || a?.slug || ''
          const slugB = b?.slug?.da || b?.slug || ''
          
          // Strip trailing -2, -3 etc. for matching with storyOrder
          const cleanSlugA = slugA.replace(/-\d+$/, '')
          const cleanSlugB = slugB.replace(/-\d+$/, '')
          
          const indexA = storyOrder.indexOf(cleanSlugA)
          const indexB = storyOrder.indexOf(cleanSlugB)

          if (indexA === -1 && indexB === -1) return 0
          if (indexA === -1) return 1
          if (indexB === -1) return -1
          return indexA - indexB
        })

        // Sort accordions by order
        const sortedAccordions = [...accordionsData].sort((a, b) => {
          const orderA = typeof a?.order === 'number' ? a.order : 999
          const orderB = typeof b?.order === 'number' ? b.order : 999
          return orderA - orderB
        })

        setPages(pagesData)
        setPatienthistorier(sortedStories)
        setGalleries(galleriesData)
        setBehandlingsmetoder(sortedTreatments)
        setSygdommeLidelser(sortedIllnesses)
        setAccordions(sortedAccordions)
      } catch (err) {
        if (!alive) return
        setError(err instanceof Error ? err.message : 'Der opstod en fejl under indlæsningen.')
      } finally {
        if (alive) setLoading(false)
      }
    }

    run()

    return () => {
      alive = false
    }
  }, [])

  const value = useMemo(() => {
    const getPage = (slug) => findCmsPageBySlug(pages, slug)
    const getStory = (slug) => patienthistorier.find((story) => story?.slug?.da === slug)
    const getGallery = (slug) => galleries.find((gallery) => gallery?.slug?.da === slug)
    const getAccordionsForPage = (targetSlug) => {
      if (!targetSlug) return []
      const cleanTarget = String(targetSlug).replace(/^\//, '').replace(/^behandlingsmetoder\//, '').replace(/^sygdomme-lidelser\//, '')
      return accordions.filter((item) => {
        const itemPage = String(item?.page || '').replace(/^\//, '').replace(/^behandlingsmetoder\//, '').replace(/^sygdomme-lidelser\//, '')
        return itemPage === cleanTarget || item?.page === targetSlug
      })
    }

    return {
      pages,
      patienthistorier,
      galleries,
      behandlingsmetoder,
      sygdommeLidelser,
      accordions,
      loading,
      error,
      getPage,
      getStory,
      getGallery,
      getAccordionsForPage,
    }
  }, [pages, patienthistorier, galleries, behandlingsmetoder, sygdommeLidelser, accordions, loading, error])

  return <SiteDataContext.Provider value={value}>{children}</SiteDataContext.Provider>
}

export function useSiteData() {
  const context = useContext(SiteDataContext)
  if (!context) {
    throw new Error('useSiteData skal bruges inde i SiteDataProvider')
  }

  return context
}
